import type { FC, FocusEvent } from 'react';
import { useEffect, useMemo, useRef } from 'react';

import type { ComboBoxOptions } from '@interstate/components/ComboBox';
import { ComboBox } from '@interstate/components/ComboBox';
import { Grid } from '@interstate/components/Grid';
import { VinInput } from '@interstate/components/VinInput';
import type { InterstateOnChangeCallback, InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import type { DropdownOptionWithDescription } from '@makemydeal/dr-shared-components';
import { Dropdown } from '@makemydeal/dr-shared-components';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import {
    deferredSelect,
    selectFocusedInput,
    useSelectOnFocusEnabledWithLegacyFallback
} from '@makemydeal/dr-shared-components/select-on-focus';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { PriorBalanceFormData } from './usePriorBalanceForm';

import {
    PRIOR_BALANCE_AMOUNT_LABEL,
    PRIOR_BALANCE_LENDER_OWED_LABEL,
    PRIOR_BALANCE_LENDER_OWED_PLACEHOLDER,
    PRIOR_BALANCE_MAX_AMOUNT,
    PRIOR_BALANCE_MAX_AMOUNT_MESSAGE,
    PRIOR_BALANCE_PAYABLE_TO_LABEL,
    PRIOR_BALANCE_PAYABLE_TO_PLACEHOLDER,
    PRIOR_BALANCE_VIN_LABEL
} from './constants';
import { FieldsBlock } from './priorBalance.styles';

export const getSelectedComboBoxValue = (options: ComboBoxOptions): string => options[0]?.value ?? '';

export type PriorBalanceFieldsProps = {
    formData: PriorBalanceFormData;
    lenderOptions: DropdownOptionWithDescription[];
    onFieldChange: <K extends keyof PriorBalanceFormData>(field: K, value: PriorBalanceFormData[K]) => void;
};

export const PriorBalanceFields: FC<PriorBalanceFieldsProps> = ({ formData, lenderOptions, onFieldChange }) => {
    const isAutoSelectEnabled = useSelectOnFocusEnabledWithLegacyFallback(
        featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap
    );
    const payableToInputRef = useRef(formData.payableTo || '');

    useEffect(() => {
        payableToInputRef.current = formData.payableTo || '';
    }, [formData.payableTo]);

    const payableToValue = useMemo<ComboBoxOptions>(
        () => (formData.payableTo ? [{ label: formData.payableTo, value: formData.payableTo }] : []),
        [formData.payableTo]
    );

    const handleVinFocus = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (isAutoSelectEnabled) deferredSelect(event.currentTarget);
    };

    const handlePayableToFocus = (event: FocusEvent<HTMLElement>) => {
        if (isAutoSelectEnabled) selectFocusedInput(event);
    };

    const commitPayableTo = (value: string) => {
        payableToInputRef.current = value;
        onFieldChange('payableTo', value || undefined);
    };

    const handlePayableToChange = (event: InterstateOnChangeEvent<ComboBoxOptions>) => {
        commitPayableTo(getSelectedComboBoxValue(event.target.value));
    };

    const handlePayableToInput: InterstateOnChangeCallback<string> = (event) => {
        payableToInputRef.current = event.target.value;
    };

    const handlePayableToBlur = () => {
        if (payableToInputRef.current !== (formData.payableTo || '')) {
            commitPayableTo(payableToInputRef.current);
        }
    };

    const handleLenderOwedChange: InterstateOnChangeCallback<SelectInputEventValue> = (event) => {
        onFieldChange('lenderOwed', event.target.value || undefined);
    };

    return (
        <FieldsBlock>
            <Grid container columnSpacing={1} rowSpacing={2}>
                <Grid xs={12} xl={4}>
                    <NumericInput
                        name="priorBalanceAmount"
                        label={PRIOR_BALANCE_AMOUNT_LABEL}
                        data-testid="prior-balance-amount-input"
                        size="sm"
                        inputPrefix="$"
                        allowDecimal
                        decimalMaxLength={2}
                        allowNegative={false}
                        maxValue={PRIOR_BALANCE_MAX_AMOUNT}
                        customErrorMessages={{ exceedsMaxValueRule: PRIOR_BALANCE_MAX_AMOUNT_MESSAGE }}
                        value={typeof formData.amount === 'number' ? formData.amount.toString() : ''}
                        onChange={(event: InterstateOnChangeEvent<string>) => {
                            const raw = event.target.value;
                            onFieldChange('amount', raw === '' || raw == null ? undefined : Number(raw));
                        }}
                        selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                    />
                </Grid>
                <Grid xs={12} xl={4}>
                    <div onFocus={handlePayableToFocus} data-testid="prior-balance-payable-to-container">
                        <ComboBox
                            name="priorBalancePayableTo"
                            label={PRIOR_BALANCE_PAYABLE_TO_LABEL}
                            placeholder={PRIOR_BALANCE_PAYABLE_TO_PLACEHOLDER}
                            data-testid="prior-balance-payable-to-input"
                            size="sm"
                            displayLabel
                            allowNewEntry
                            options={lenderOptions}
                            value={payableToValue}
                            onChange={handlePayableToChange}
                            onInputChange={handlePayableToInput}
                            onBlur={handlePayableToBlur}
                            sx={{ '& .ids-combo-box-multi-select-input': { padding: 0 } }}
                        />
                    </div>
                </Grid>
                <Grid xs={12} xl={4}>
                    <VinInput
                        name="priorBalanceVin"
                        label={PRIOR_BALANCE_VIN_LABEL}
                        data-testid="prior-balance-vin-input"
                        size="sm"
                        showIcon
                        value={formData.vin || ''}
                        onFocus={handleVinFocus}
                        onChange={(event: InterstateOnChangeEvent<string>) => onFieldChange('vin', event.target.value || undefined)}
                    />
                </Grid>
                <Grid xs={12} xl={4}>
                    <Dropdown
                        name="priorBalanceLenderOwed"
                        label={PRIOR_BALANCE_LENDER_OWED_LABEL}
                        placeholder={PRIOR_BALANCE_LENDER_OWED_PLACEHOLDER}
                        data-testid="prior-balance-lender-owed-input"
                        size="sm"
                        allowRemoval
                        options={lenderOptions}
                        value={formData.lenderOwed || ''}
                        onChange={handleLenderOwedChange}
                        selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                    />
                </Grid>
            </Grid>
        </FieldsBlock>
    );
};
